'use client';

import { c as _c } from "react/compiler-runtime";
import { ZERO } from '@morpho-org/dapp-core';
import { useEffect, useState } from 'react';
import { erc4626Abi } from 'viem';
import { useReadContracts } from 'wagmi';
import { DEPRECATED_VAULTS, type DeprecatedVault } from '@/config/deprecated-vaults';
import { useWeb3Context } from '@/contexts/Web3Context';

// Stable identity for the disconnected / loading / failed paths.
const NO_POSITIONS: readonly DeprecatedVault[] = [];
const POLL_INTERVAL = 30_000;
const STALE_TIME = 10_000;
type ContractResult = {
  status: string;
  result?: unknown;
} | undefined;
const readAmount = (result: ContractResult): bigint | undefined => result?.status === 'success' && typeof result.result === 'bigint' ? result.result : undefined;
const hasFailure = (results: readonly ContractResult[] | undefined) => results?.some(result => result?.status === 'failure') === true;

/**
 * The deprecated vaults the connected wallet still holds a redeemable position in.
 *
 * Read on-chain rather than from the positions API: the API has no vault filter, and
 * since withdrawing is the only way to clear the (undismissable) toast this feeds, a
 * lagging indexer would strand users who had already exited.
 *
 * Shares alone are not a position: a full withdraw leaves a few shares behind that
 * redeem for nothing, so shares are converted to assets and compared against the
 * vault's dust floor.
 *
 * Fails open: a failed read reads as "no position", so the prompt is skipped rather
 * than shown in error. A failed refetch is the exception. TanStack Query keeps the
 * last successful data, so a holder keeps the toast through an RPC blip while the
 * poll retries.
 */
export const useDeprecatedVaultPositions = () => {
  const $ = _c(21);
  const {
    account
  } = useWeb3Context();
  const address = account.isConnected ? account.address : undefined;
  const [isPolling, setIsPolling] = useState(false);
  const refetchInterval = isPolling ? POLL_INTERVAL : false;
  let t0;
  if ($[0] !== address) {
    t0 = DEPRECATED_VAULTS.map(vault => ({
      address: vault.address,
      chainId: vault.chainId,
      abi: erc4626Abi,
      functionName: "balanceOf",
      args: [address] as const
    }));
    $[0] = address;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const t1 = !!address;
  let t2;
  if ($[2] !== refetchInterval || $[3] !== t1) {
    t2 = {
      enabled: t1,
      staleTime: STALE_TIME,
      refetchInterval
    };
    $[2] = refetchInterval;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t0 || $[6] !== t2) {
    t3 = {
      contracts: t0,
      query: t2
    };
    $[5] = t0;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const {
    data: shares,
    isError: sharesFailed
  } = useReadContracts(t3);
  let t4;
  if ($[8] !== shares) {
    t4 = (vault_0, index) => {
      const balance = readAmount(shares?.[index]);
      return balance !== undefined && balance > ZERO ? [{
        vault: vault_0,
        balance
      }] : [];
    };
    $[8] = shares;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const candidates = DEPRECATED_VAULTS.flatMap(t4);
  const {
    data: assets,
    isError: assetsFailed
  } = useReadContracts({
    contracts: candidates.map(_temp),
    query: {
      enabled: candidates.length > 0,
      staleTime: STALE_TIME,
      refetchInterval
    }
  });
  let t5;
  if ($[10] !== assets) {
    t5 = (t6, index_0) => {
      const {
        vault: vault_2
      } = t6;
      const redeemable = readAmount(assets?.[index_0]);
      return redeemable !== undefined && redeemable >= vault_2.minAssets;
    };
    $[10] = assets;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const positions = candidates.filter(t5).map(_temp2);
  let t6;
  if ($[12] !== assets || $[13] !== assetsFailed || $[14] !== positions.length || $[15] !== shares || $[16] !== sharesFailed) {
    t6 = sharesFailed || assetsFailed || hasFailure(shares) || hasFailure(assets) || positions.length > 0;
    $[12] = assets;
    $[13] = assetsFailed;
    $[14] = positions.length;
    $[15] = shares;
    $[16] = sharesFailed;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  const shouldPoll = t6;
  let t7;
  let t8;
  if ($[18] !== shouldPoll) {
    t7 = () => {
      setIsPolling(shouldPoll);
    };
    t8 = [shouldPoll];
    $[18] = shouldPoll;
    $[19] = t7;
    $[20] = t8;
  } else {
    t7 = $[19];
    t8 = $[20];
  }
  useEffect(t7, t8);
  return positions.length > 0 ? positions : NO_POSITIONS;
};
function _temp(t0) {
  const {
    vault: vault_1,
    balance: balance_0
  } = t0;
  return {
    address: vault_1.address,
    chainId: vault_1.chainId,
    abi: erc4626Abi,
    functionName: "convertToAssets",
    args: [balance_0] as const
  };
}
function _temp2(t0) {
  const {
    vault: vault_3
  } = t0;
  return vault_3;
}